import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Receipt, Check, Download, Trash2, Filter } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/useConfirm";
import { api, API_BASE } from "@/lib/api";
import { EXPENSE_CATEGORIES, EXPENSE_CAT_LABEL as CAT_LABEL } from "@/lib/expenseCategories";
import { ExpenseDateFilter, expenseFilterRange } from "@/components/ExpenseDateFilter";

export default function AdminExpenses() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [catFilter, setCatFilter] = useState("ALL");
  const [dateFilter, setDateFilter] = useState({ mode: "ALL", date: "", month: "", year: String(new Date().getFullYear()) });
  const [active, setActive] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== "ALL") params.status = statusFilter;
      if (catFilter !== "ALL") params.category = catFilter;
      const range = expenseFilterRange(dateFilter);
      if (range) { params.start = range.start; params.end = range.end; }
      const { data } = await api.get("/admin/expenses", { params });
      setRows(data.expenses || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [statusFilter, catFilter, dateFilter]);
  useEffect(() => { load(); }, [load]);

  const totals = rows.reduce(
    (acc, r) => {
      acc.amount += Number(r.amount_nzd || 0);
      acc.gst += Number(r.gst_nzd || 0);
      return acc;
    },
    { amount: 0, gst: 0 }
  );

  const download = (row) => {
    window.open(`${API_BASE}/admin/expenses/${row.id}/download`, "_blank");
  };

  const review = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/expenses/${active.id}/review`, { notes });
      toast.success("Marked reviewed");
      setActive(null); setNotes(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Review failed"); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: "Delete expense?",
      description: `Permanently remove "${row.vendor || row.filename}" for ${row.manager_name}.`,
      confirmText: "Delete",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/expenses/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Receipt className="h-5 w-5 text-[#1A2744]" />
        <h1 className="text-xl font-bold text-[#1A2744]">Expenses</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-gray-500">Receipts</div>
          <div className="text-2xl font-bold text-[#1A2744]">{rows.length}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-gray-500">Total (NZD)</div>
          <div className="text-2xl font-bold text-[#1A2744]">${totals.amount.toFixed(2)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-gray-500">Total GST (NZD)</div>
          <div className="text-2xl font-bold text-[#C9A84C]">${totals.gst.toFixed(2)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-gray-500">Pending review</div>
          <div className="text-2xl font-bold text-amber-700">{rows.filter((r) => r.status === "SUBMITTED").length}</div>
        </CardContent></Card>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-gray-500" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] h-9" data-testid="admin-expenses-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            <SelectItem value="SUBMITTED">Submitted</SelectItem>
            <SelectItem value="REVIEWED">Reviewed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-[180px] h-9" data-testid="admin-expenses-cat-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All categories</SelectItem>
            {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <ExpenseDateFilter value={dateFilter} onChange={setDateFilter} idPrefix="admin-expense-filter" />
      </div>

      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Submitted by</TableHead><TableHead>Date</TableHead>
            <TableHead>Category</TableHead><TableHead>Vendor</TableHead>
            <TableHead>Amount</TableHead><TableHead>GST</TableHead>
            <TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-6">No expenses</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`admin-expense-row-${r.id}`}>
                <TableCell className="text-sm">{r.manager_name} <span className="text-gray-400 text-[11px] ml-1">{r.site}</span></TableCell>
                <TableCell className="text-xs">{r.purchase_date || r.created_at?.slice(0, 10)}</TableCell>
                <TableCell className="text-xs">{CAT_LABEL[r.category] || r.category}</TableCell>
                <TableCell className="text-xs">{r.vendor || "—"}</TableCell>
                <TableCell className="text-xs">{r.amount_nzd != null ? `$${Number(r.amount_nzd).toFixed(2)}` : "—"}</TableCell>
                <TableCell className="text-xs">{r.gst_nzd != null ? `$${Number(r.gst_nzd).toFixed(2)}` : "—"}</TableCell>
                <TableCell><span className={`px-2 py-0.5 rounded font-mono text-[10px] ${r.status === "REVIEWED" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{r.status}</span></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => download(r)} data-testid={`admin-expense-view-${r.id}`}>
                    <Download className="h-3 w-3 mr-1" /> View
                  </Button>
                  {r.status === "SUBMITTED" && (
                    <Button size="sm" variant="outline" onClick={() => { setActive(r); setNotes(""); }} className="ml-2 text-emerald-700 border-emerald-300" data-testid={`admin-expense-review-${r.id}`}>
                      <Check className="h-3 w-3 mr-1" /> Review
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => remove(r)} className="ml-2 text-red-600 border-red-300" data-testid={`admin-expense-delete-${r.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {active && (
        <Dialog open={true} onOpenChange={(v) => { if (!v) { setActive(null); setNotes(""); } }}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Mark expense reviewed</DialogTitle></DialogHeader>
            <div className="text-sm space-y-1">
              <div><strong>Manager:</strong> {active.manager_name}</div>
              <div><strong>Vendor:</strong> {active.vendor || "—"}</div>
              <div><strong>Amount:</strong> ${Number(active.amount_nzd || 0).toFixed(2)} (GST ${Number(active.gst_nzd || 0).toFixed(2)})</div>
              {active.description && <div><strong>Description:</strong> {active.description}</div>}
            </div>
            <Textarea rows={3} placeholder="Review notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-expense-notes" />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setActive(null); setNotes(""); }}>Cancel</Button>
              <Button onClick={review} disabled={busy} className="bg-emerald-600 text-white" data-testid="admin-expense-review-confirm">
                <Check className="h-3.5 w-3.5 mr-1" /> Mark reviewed
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
